import type { CompProps, GenericFieldConfig } from '@neo/blocks/forms/types'
import { Alert } from '@neo/primitives/alert'
import { SignalHigh, SignalLow, SignalMedium, TriangleAlertIcon } from 'lucide-react'
import { FlowTypes } from 'v2/types'
import { AudioPrompt } from '../components/audio-prompt'
import { NodevarList } from '../components/nodevar-list'
import { v2validations } from '../config/validation.config'
import { useStartNodeType } from '../hooks/use-start-node-type'
import type { CreateTaskNodeData, SidebarComponentProps } from '../types'
import { Configuration } from './create-task/configuration'
import { GenericSidebarComponent } from './sidebar.base'

type FormValues = CreateTaskNodeData['config']

const fields = (nodeVars: string[], type: FlowTypes) => {
  const validations = v2validations({ nodeVars }).create_task
  const base: GenericFieldConfig<FormValues> = [
    {
      name: 'create_task_type',
      componentType: 'select',
      label: (
        <span className="flex items-center gap-2">
          Task type <span className="text-status-danger">*</span>
        </span>
      ),
      className: 'col-span-1',
      zodValidation: validations.shape.create_task_type,
      inputProps: {
        options: [
          { label: 'Follow up', value: 'Follow_up' },
          { label: 'Internal', value: 'Internal' },
        ],
        searchable: false,
      },
      afterOnChange: (values, _, form) => {
        if (values.create_task_type === 'Internal') {
          form?.setFieldValue('channel', null)
        } else {
          form?.setFieldValue('channel', 'call')
        }
        form?.validateField('channel', 'change')
      },
    },
    {
      name: 'channel',
      componentType: 'select',
      label: (
        <span className="flex items-center gap-2">
          Channel <span className="text-status-danger">*</span>
        </span>
      ),
      className: 'col-span-1',
      zodValidation: validations.shape.channel,
      onChangeListenTo: ['create_task_type'],
      visible: (values) => values.create_task_type === 'Follow_up',
      inputProps: {
        options: [
          { label: 'Call', value: 'call' },
          { label: 'Message', value: 'message' },
          { label: 'WhatsApp', value: 'whatsapp' },
        ],
        searchable: false,
      },
    },
    {
      label: 'Description',
      name: 'message',
      componentType: 'textarea',
      zodValidation: validations.shape.message,
      inputProps: {
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            className="pr-2"
            inputElementSelector="[name=message]"
            sideOffset={470}
          />
        ),
      },
    },
    {
      name: 'priority',
      componentType: 'select',
      zodValidation: validations.shape.priority,
      className: 'col-span-1',
      label: 'Priority',
      inputProps: {
        options: [
          { label: 'Low', value: 'Low', icon: <SignalLow className="text-brand-text" /> },
          { label: 'Medium', value: 'Medium', icon: <SignalMedium className="text-brand-text" /> },
          { label: 'High', value: 'High', icon: <SignalHigh className="text-brand-text" /> },
          {
            label: 'Critical',
            value: 'Critical',
            icon: <TriangleAlertIcon size={20} className="text-status-danger" />,
          },
        ],
        searchable: false,
      },
    },
    {
      name: 'due_date',
      type: 'basic',
      component: ({ form }) => <Configuration form={form} />,
    },
    // {
    //   name: 'due_date.due_date_time_unit',
    //   componentType: 'select',
    //   className: 'inline-flex w-[89px]',
    //   label: '',
    //   zodValidation: validations.shape.due_date.due_date_time_unit,
    //   inputProps: {
    //     options: [
    //       { label: 'Hour(s)', value: 'hours' },
    //       { label: 'Day(s)', value: 'days' },
    //     ],
    //     searchable: false,
    //   },
    // },
    // {
    //   name: 'configuration',
    //   type: 'basic',
    //   component: ({ form }) => <Configuration form={form} />,
    // },
  ]
  // Add prompt if incoming call
  if (type === FlowTypes.incoming_call) {
    base.push({
      name: 'alert info',
      type: 'slot',
      component: (
        <Alert
          variant="warning"
          className="border-none col-span-2"
          content="The call with the customer will be dropped and the success prompt will play after the callback request is created."
        />
      ),
    })
    base.push({
      label: 'Success Prompt',
      name: 'prompt',
      type: 'custom',
      component: (props) => (
        <AudioPrompt
          {...(props as CompProps<FormValues, FormValues['prompt']>)}
          nodeVars={nodeVars}
        />
      ),
      zodValidation: validations.shape.prompt,
      eventDataExtractor: (data) => data as Record<string, unknown>,
    })
  }
  return base
}

// NODE_GEN: Update this code, do not commit directly
// Remove this comment after updating the code

export const CreateTaskSidebarComponent = (props: SidebarComponentProps<CreateTaskNodeData>) => {
  const { nodeVars } = props
  const type = useStartNodeType()
  const formFields = fields(nodeVars, type as FlowTypes)

  return <GenericSidebarComponent fields={formFields} {...props} />
}
